import { useState } from 'react';
import { toast } from 'sonner';
import { Settings2, X, Check } from 'lucide-react';
import { useAdminCompanies, useSetCustomPlan } from '@/api/queries/superAdmin.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

function EditTermsModal({ company, onClose }) {
  const { mutate, isPending } = useSetCustomPlan();
  const existing = company.custom_plan_config || {};
  const [form, setForm] = useState({
    price_per_employee: existing.price_per_employee ?? '',
    employee_limit: existing.employee_limit ?? '',
    lens_device_limit: existing.lens_device_limit ?? '',
    company_limit: existing.company_limit ?? '',
  });
  const inputCls = 'w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
  const numOrUndef = (v) => (v === '' ? undefined : Number(v));

  const handleSave = () => {
    mutate({
      companyId: company.id,
      price_per_employee: numOrUndef(form.price_per_employee),
      employee_limit: numOrUndef(form.employee_limit),
      lens_device_limit: numOrUndef(form.lens_device_limit),
      company_limit: numOrUndef(form.company_limit),
    }, {
      onSuccess: () => { toast.success(`${company.name}'s terms updated — live immediately`); onClose(); },
      onError: () => toast.error('Failed to save terms'),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[16px] font-bold text-[var(--text-primary)]">{company.name}</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-[12px] text-[var(--text-secondary)]">Leave a field blank to fall back to the shared Custom plan defaults.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">₹/employee/mo</label>
              <input className={inputCls} type="number" placeholder="Default" value={form.price_per_employee} onChange={e => setForm(f => ({ ...f, price_per_employee: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Employee limit</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.employee_limit} onChange={e => setForm(f => ({ ...f, employee_limit: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Lens device limit</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.lens_device_limit} onChange={e => setForm(f => ({ ...f, lens_device_limit: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">No. of companies</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.company_limit} onChange={e => setForm(f => ({ ...f, company_limit: e.target.value }))} />
            </div>
          </div>
          <Button onClick={handleSave} disabled={isPending} className="w-full">
            <Check size={14}/> {isPending ? 'Saving…' : 'Save Terms'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminCustomCompaniesPage() {
  const { data, isLoading } = useAdminCompanies('CUSTOM');
  const [editing, setEditing] = useState(null);
  const companies = data?.data ?? [];

  return (
    <div className="space-y-5 fade-in max-w-5xl">
      <PageHeader
        title="Custom Companies"
        subtitle="Every Custom customer can have its own pricing, limits and feature set"
        breadcrumb={[{ label: 'Super Admin', href: '/super-admin' }, { label: 'Custom Companies' }]}
      />

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <p className="text-[13px] text-[var(--text-secondary)] p-6 text-center">Loading…</p>
        ) : companies.length === 0 ? (
          <p className="text-[13px] text-[var(--text-secondary)] p-6 text-center">No Custom-plan companies yet.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Company</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Owner</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Terms</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Renews</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors">
                  <td className="px-5 py-3 font-semibold text-[var(--text-primary)]">{c.name}</td>
                  <td className="px-5 py-3 text-[var(--text-secondary)]">{c.owner?.name || '—'}</td>
                  <td className="px-5 py-3 text-[var(--text-tertiary)]">
                    {c.custom_plan_config
                      ? Object.entries(c.custom_plan_config).filter(([,v]) => v != null).map(([k,v]) => `${k}: ${v}`).join(', ') || 'Custom (empty)'
                      : 'Using shared defaults'}
                  </td>
                  <td className="px-5 py-3 text-[var(--text-tertiary)]">
                    {c.plan_expires_at ? new Date(c.plan_expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <Button size="xs" variant="outline" onClick={() => setEditing(c)}><Settings2 size={12}/> Edit Terms</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <EditTermsModal company={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
